import React, { useMemo, useState } from "react";
import WorkflowTaskStage from "./WorkflowTaskStage";
import WorkflowCaseActionBanner, { WorkflowCaseActionBannerKind } from "./WorkflowCaseActionBanner";
import { WorkflowCanvas } from "./workflowBuilderData";
import { WorkflowLiveCase } from "./workflowLiveCase";
import useWorkflowCaseAction from "../../hooks/useWorkflowCaseAction";
import { completeWorkflowRunTask } from "../../services/workflowService";
import { WorkflowInstanceTaskDto } from "../../types/workflowDto";

interface WorkflowTaskLiveProps {
  canvas: WorkflowCanvas;
  liveCase: WorkflowLiveCase;
  /** The case's materialized tasks for this stage — matched to the canvas checklist by taskKey. */
  tasks: WorkflowInstanceTaskDto[];
}

const isFinished = (task: WorkflowInstanceTaskDto | undefined) =>
  task?.status === "completed" || task?.status === "skipped";

/**
 * A Task stage on a real case. Ticking a task completes it on the engine (task/complete); a
 * completed task is final — there is no un-complete — so it stays ticked and locked. When the
 * last one completes the engine advances the case itself, and the reload shows where it went.
 */
const WorkflowTaskLive: React.FC<WorkflowTaskLiveProps> = ({ canvas, liveCase, tasks }) => {
  const { busy, run } = useWorkflowCaseAction();
  const [banner, setBanner] = useState<WorkflowCaseActionBannerKind | null>(null);
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null);

  const rowFor = useMemo(() => {
    const byKey = new Map(tasks.map((task) => [task.taskKey ?? "", task]));
    return (taskId: string) => byKey.get(taskId);
  }, [tasks]);

  const completed = useMemo(
    () => Object.fromEntries(canvas.tasks.map((task) => [task.id, isFinished(rowFor(task.id))])),
    [canvas.tasks, rowFor],
  );
  // Finished tasks are final, and a task the case hasn't materialized can't be completed yet.
  const lockedTaskIds = useMemo(
    () => canvas.tasks.filter((task) => !rowFor(task.id) || isFinished(rowFor(task.id))).map((task) => task.id),
    [canvas.tasks, rowFor],
  );

  const reload = async () => {
    setBanner((await liveCase.onChanged()) ? null : "reload-failed");
  };

  const handleToggle = async (taskId: string, done: boolean) => {
    const row = rowFor(taskId);
    if (!done || !row || isFinished(row)) return;
    const outcome = await run(completeWorkflowRunTask, { taskId: row.id });
    if (!outcome) return;
    if (outcome.kind !== "done") {
      setInvalidMessage(outcome.kind === "invalid" ? outcome.message : null);
      setBanner(outcome.kind);
      return;
    }
    await reload();
  };

  return (
    <WorkflowTaskStage
      canvas={canvas}
      completed={completed}
      lockedTaskIds={lockedTaskIds}
      busy={busy}
      onToggle={(taskId, done) => void handleToggle(taskId, done)}
      // The engine moves the case once every task is done; Continue just shows where it went.
      onContinue={() => void reload()}
      banner={
        <WorkflowCaseActionBanner
          kind={banner}
          forbiddenMessage="This task is assigned to someone else, so you can't complete it."
          invalidMessage={invalidMessage}
          onReload={() => void reload()}
        />
      }
    />
  );
};

export default WorkflowTaskLive;
